<template>
  <default-layout>
    <div class="listing pt-4">
      <b-container>
        <b-alert
          variant="primary"
          fade
          :show="listing.isOwnListing && listing.isPublic">
          This listing belongs to you. You cannot favorite or
          submit purchase requests for listings that you have created.
        </b-alert>
        <b-alert
          variant="primary"
          fade
          :show="listing && !listing.isPublic">
          You are previewing how this listing appears on ZoopLocal.
        </b-alert>
        <b-alert
          variant="warning"
          dismissible
          fade
          :show="listing.isPopular">
          <font-awesome-icon
            class="mx-1"
            :icon="['far', 'arrow-alt-circle-up']"/>
          This item is popular on Zoop!
          <a
            @click="purchaseRequest"
            class="alert-link"
            href="#">
            Submit a purchase request</a>
          soon to make it yours!
        </b-alert>
        <b-row>
          <b-col
            :cols="12"
            :lg="5">
            <b-carousel
              v-model="slide"
              img-width="500"
              img-height="500"
              style="text-shadow: 0px 0px 2px #000"
              indicators>
              <b-carousel-slide
                v-for="image in listing.images"
                :text="image.caption"
                :key="image.id">
                <template v-slot:img>
                  <a
                    :href="image.url"
                    target="_blank">
                    <b-img-lazy
                      blank-color="#F8F9FA"
                      class="d-block img-fluid w-100 listing-image"
                      width="500"
                      height="500"
                      :src="image.url"/>
                  </a>
                </template>
              </b-carousel-slide>
              </b-carousel>
              <hr>
              <h5>More Images</h5>
              <div class="py-2">
                <b-img-lazy
                  draggable="false"
                  v-for="(image, index) in listing.images"
                  width="100px"
                  class="listing-image-thumbnail mr-2"
                  :key="image.id"
                  :src="`${image.url}?t=true`"
                  alt="Image 1"
                  @click.native="handleThumbnailClick(index)"/>
              </div>
          </b-col>
          <b-col
            :cols="12"
            :lg="7">
            <div class="mb-4">
              <h1>{{listing.title}}</h1>
              <h2 class="text-primary">
                {{listing.price | currency}}
              </h2>
            </div>
            <div>
              <b-button
                @click="purchaseRequest"
                variant="primary"
                squared
                :disabled="!listing.isPublic">
                Buy on ZoopLocal
              </b-button>
              <span v-if="listing.favoritesId">
                <b-button
                  @click="removeFavorite(listing.favoritesId)"
                  variant="warning"
                  class="ml-2"
                  squared
                  :disabled="isFavoriteLoading">
                  <span v-if="isFavoriteLoading">
                    <b-spinner small/>
                  </span>
                  <span v-else>
                    <font-awesome-icon
                      :icon="['fas', 'star']"/>
                    Remove from Favorites
                  </span>
                </b-button>
              </span>
              <span v-else>
                <b-button
                  @click="addFavorite(listing.id)"
                  variant="outline-warning"
                  class="ml-2"
                  squared
                  :disabled="isFavoriteLoading || !listing.isPublic">
                  <span v-if="isFavoriteLoading">
                    <b-spinner small/>
                  </span>
                  <span v-else>
                    <font-awesome-icon
                      :icon="['fas', 'star']"/>
                    Add to Favorites
                  </span>
                </b-button>
              </span>
            </div>
            <hr>
            <h5>Description</h5>
            <p>
              {{ listing.description }}
            </p>
            <hr>
            <h5>Details</h5>
            <b-row
              v-if="listing.region"
              class="mb-1">
              <b-col
                class="text-muted"
                :cols="3">
                ZoopLocal Area
              </b-col>
              <b-col>
                {{ listing.region.name }}
              </b-col>
            </b-row>
            <b-row class="mb-1">
              <b-col
                class="text-muted"
                :cols="3">
                Category
              </b-col>
              <b-col>
                {{ listing.category }}
              </b-col>
            </b-row>
            <b-row class="mb-1">
              <b-col
                class="text-muted"
                :cols="3">
                Subcategory
              </b-col>
              <b-col>
                {{ listing.subcategory }}
              </b-col>
            </b-row>
            <b-row class="mb-1">
              <b-col
                class="text-muted"
                :cols="3">
                Material
              </b-col>
              <b-col>
                {{ listing.material }}
              </b-col>
            </b-row>
            <b-row class="mb-1">
              <b-col
                class="text-muted"
                :cols="3">
                Color
              </b-col>
              <b-col>
                {{ listing.color }}
              </b-col>
            </b-row>
            <b-row class="mb-1">
              <b-col
                class="text-muted"
                :cols="3">
                Dimensions
              </b-col>
              <b-col>
                {{ listing.dimensionW }}" w &times;
                {{ listing.dimensionH }}" h &times;
                {{ listing.dimensionD }}" d
              </b-col>
            </b-row>
            <b-row class="mb-1">
              <b-col
                class="text-muted"
                :cols="3">
                Seller's Home
              </b-col>
              <b-col>
                <span v-if="listing.conditionSmokeFree">
                  Smoke Free
                  <font-awesome-icon
                    class="mx-1"
                    :icon="['far', 'check-circle']"/>
                </span>
                <span v-else>
                  <del>
                    Smoke Free
                  </del>
                </span>
                <br>
                <span v-if="listing.conditionPetFree">
                  Pet Free
                  <font-awesome-icon
                    class="mx-1"
                    :icon="['far', 'check-circle']"/>
                </span>
                <span v-else>
                  <del>
                    Pet Free
                  </del>
                </span>
              </b-col>
            </b-row>
            <listing-delivery-summary
              class="mb-1"
              :listing="listing"/>
          </b-col>
        </b-row>
      </b-container>
    </div>
  </default-layout>
</template>

<script>
import currency from '@/filters/currency.filter'
import { mapGetters } from 'vuex'

import ListingDeliverySummary from '@/components/listings/ListingDeliverySummary.vue'

export default {
  name: 'Listing',
  components: {
    ListingDeliverySummary,
  },
  filters: {
    currency,
  },
  data() {
    return {
      slide: 0,
      isFavoriteLoading: false,
      listing: {
        title: undefined,
        description: undefined,
        price: undefined,
        category: undefined,
        subcategory: undefined,
        isPopular: undefined,
        isPublic: true,
        images: [],
      },
    }
  },
  computed: {
    ...mapGetters('Authentication', ['isAuthenticated']),
  },
  methods: {
    async addFavorite(listingsId) {
      if (!this.isAuthenticated) {
        this.$router.push({ path: '/login', query: { redirectFrom: this.$route.path } })
        return
      }
      this.isFavoriteLoading = true
      const favorite = await this.$store.dispatch('Favorites/addFavorite', listingsId)
      this.listing.favoritesId = favorite.id
      this.isFavoriteLoading = false
    },
    async removeFavorite(favoritesId) {
      this.isFavoriteLoading = true
      await this.$store.dispatch('Favorites/removeFavorite', favoritesId)
      this.listing.favoritesId = null
      this.isFavoriteLoading = false
    },
    purchaseRequest() {
      this.$router.push({
        name: 'listing-request-review',
        params: {
          id: this.$route.params.id,
          displayName: this.$route.params.displayName,
        },
      })
    },
    handleThumbnailClick(index) {
      this.slide = index
    },
  },
  async created() {
    try {
      const { data: { listing } } = await this.$http.get(`/listing/${this.$route.params.id}`)
      this.listing = listing
      document.title = `${listing.title} - ZoopLocal`
    } catch (err) {
      console.error(err)
    }
  },
}
</script>

<style lang="scss" scoped>
.listing-image-thumbnail {
  cursor: pointer;
  border-radius: 5px;
}

.listing-image {
  cursor: -moz-zoom-in;
  cursor: -webkit-zoom-in;
  cursor: zoom-in;
}
</style>
